import { Component, ReactNode } from 'react';
import { Banner, Button, Flex } from '@optiaxiom/react';
import * as Sentry from '@sentry/react';

type ErrorBoundaryProps = {
  children: ReactNode;
  fallback?: ReactNode;
  onReset?: () => void;
};

type ErrorBoundaryState = {
  hasError: boolean;
  error: Error | null;
};

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    Sentry.captureException(error, {
      contexts: {
        react: {
          componentStack: errorInfo.componentStack,
        },
      },
    });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
    this.props.onReset?.();
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <Banner intent="danger">
          <Flex flexDirection="row" alignItems="center" justifyContent="space-between" w="full">
            Something went wrong
            <Flex flexDirection="row" gap="8">
              <Button size="sm" appearance="subtle" onClick={this.handleReset}>
                Try again
              </Button>
              <Button size="sm" appearance="subtle" onClick={() => window.location.reload()}>
                Reload
              </Button>
            </Flex>
          </Flex>
        </Banner>
      );
    }

    return this.props.children;
  }
}
